import { useEffect, useState } from "react";
import { Broom, Warning, Lock, Eye, EyeSlash } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import Spinner from "@/components/Spinner";
import { toast } from "sonner";

/**
 * Dialogo destructivo con DOBLE confirmación:
 *   Paso 1  → advertencia + checkbox "Entiendo que…"
 *   Paso 2  → contraseña del usuario (verifica en backend)
 * onClearAll(password) debe lanzar en caso de contraseña inválida — el
 * componente muestra el error inline sin cerrar el diálogo.
 */
export default function ClearAllDialog({ onClearAll }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const [understand, setUnderstand] = useState(false);
  const [pwd, setPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) {
      // Reset state al cerrar
      setStep(1);
      setUnderstand(false);
      setPwd("");
      setShowPwd(false);
      setBusy(false);
      setErr("");
    }
  }, [open]);

  const goNext = () => {
    if (!understand) {
      toast.warning("Debes confirmar que entiendes las consecuencias");
      return;
    }
    setStep(2);
  };

  const submit = async () => {
    setErr("");
    if (!pwd.trim()) {
      setErr("Ingresa tu contraseña para confirmar");
      return;
    }
    setBusy(true);
    try {
      await onClearAll(pwd);
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.detail || "No se pudo eliminar. Verifica tu contraseña.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive"
          data-testid="clear-all-btn"
        >
          <Broom size={16} weight="duotone" className="mr-2" /> Eliminar todos los datos
        </Button>
      </DialogTrigger>

      <DialogContent data-testid="clear-all-dialog" className="max-w-md">
        {step === 1 ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading">
                <Warning size={22} weight="duotone" className="text-destructive" />
                Eliminar todos los datos
              </DialogTitle>
              <DialogDescription className="pt-1">
                Estás a punto de borrar <span className="font-semibold text-foreground">
                todos tus deudores y comprobantes</span> de forma permanente. Esta acción
                <span className="font-semibold text-destructive"> no se puede deshacer</span>.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border border-destructive/30 bg-destructive/5 p-3 space-y-2 text-xs text-foreground/80">
              <div>• Se eliminarán todos los registros de cartera vencida y venta a crédito.</div>
              <div>• Se eliminarán todos los comprobantes de pago cargados.</div>
              <div>• Los datos de otros usuarios no se ven afectados.</div>
            </div>

            <label className="flex items-start gap-2 pt-2 cursor-pointer select-none" htmlFor="clear-all-understand">
              <Checkbox
                id="clear-all-understand"
                checked={understand}
                onCheckedChange={(v) => setUnderstand(!!v)}
                data-testid="clear-all-understand"
              />
              <span className="text-sm leading-snug">
                Entiendo que esta acción es permanente y no puedo recuperar los datos eliminados.
              </span>
            </label>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} data-testid="clear-all-cancel">
                Cancelar
              </Button>
              <Button
                onClick={goNext}
                disabled={!understand}
                data-testid="clear-all-next"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Continuar
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 font-heading">
                <Lock size={22} weight="duotone" className="text-destructive" />
                Confirma con tu contraseña
              </DialogTitle>
              <DialogDescription className="pt-1">
                Por seguridad, ingresa la contraseña de tu cuenta para eliminar definitivamente todos los datos.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 pt-1">
              <div>
                <Label htmlFor="clear-all-password">Tu contraseña <span className="text-destructive">*</span></Label>
                <div className="relative">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="clear-all-password"
                    type={showPwd ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    autoComplete="current-password"
                    onKeyDown={(e) => e.key === "Enter" && !busy && submit()}
                    autoFocus
                    data-testid="clear-all-password"
                    className="pl-9 pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPwd((v) => !v)}
                    aria-label={showPwd ? "Ocultar" : "Mostrar"}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                  >
                    {showPwd ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
                  </button>
                </div>
                {err && (
                  <p className="text-xs text-destructive mt-2" data-testid="clear-all-error">
                    {err}
                  </p>
                )}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep(1)} disabled={busy} data-testid="clear-all-back">
                Atrás
              </Button>
              <Button
                onClick={submit}
                disabled={busy || !pwd}
                data-testid="clear-all-confirm"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {busy ? <Spinner size="s" className="mr-2" /> : <Warning size={14} weight="bold" className="mr-2" />}
                {busy ? "Eliminando…" : "Eliminar todo"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
